import React, { useState } from 'react';
import { consentFormAPI } from '../../services/api';
import { format } from 'date-fns';

// Default text for each section (used as fallback if therapist did not customise)
const DEFAULT_SECTIONS = [
  {
    key: 'section_services_text',
    title: '1. Nature of Counseling Services',
    text: `Therapy involves a collaborative relationship between therapist and client aimed at supporting emotional well-being and personal growth. Sessions typically last 50 minutes and may include talk therapy, psychoeducation, and evidence-based techniques. The therapist's role is to listen, guide, and support — not to give advice or make decisions on your behalf.`
  },
  {
    key: 'section_confidentiality_text',
    title: '2. Confidentiality',
    text: `All information shared in therapy is kept strictly confidential. Exceptions include situations where disclosure is legally required: (a) risk of imminent harm to yourself or others, (b) mandated reporting of child or elder abuse, or (c) a valid court order. In these limited circumstances, only information necessary to prevent harm or comply with legal obligations will be shared.`
  },
  {
    key: 'section_risks_text',
    title: '3. Risks & Benefits',
    text: `Therapy can be highly beneficial, but it may also involve temporary emotional discomfort as you explore difficult topics. Progress is not always linear. Benefits include improved mental health, coping strategies, and quality of life. You have the right to discuss concerns about the treatment approach at any time.`
  },
  {
    key: 'section_fees_text',
    title: '4. Session Fees & Cancellation Policy',
    text: `Session fees will be discussed and agreed upon before therapy begins. Payment is expected at the time of each session unless other arrangements are made. Please provide at least 24 hours' notice to cancel or reschedule a session. Late cancellations or no-shows may be charged the full session fee.`
  },
  {
    key: 'section_emergency_text',
    title: '5. Emergency Procedures',
    text: `This service is not a crisis line and is not designed for psychiatric emergencies. If you are in immediate danger or experiencing a mental health crisis, please contact emergency services (112/911), go to your nearest emergency room, or call a crisis helpline. Your therapist will discuss local emergency resources with you.`
  },
  {
    key: 'section_rights_text',
    title: '6. Client Rights',
    text: `You have the right to ask questions about your treatment at any time, to withdraw consent and discontinue therapy at any time without penalty, to request a referral to another therapist or service, and to access information about your records as permitted by law. Your participation in therapy is entirely voluntary.`
  }
];

// Build section list: prefer sections_json (dynamic), fall back to fixed columns, then defaults
const buildSections = (form) => {
  if (form && Array.isArray(form.sections_json) && form.sections_json.length > 0) {
    return form.sections_json.map(s => ({ title: s.title, text: s.text }));
  }
  return DEFAULT_SECTIONS.map(s => ({
    title: s.title,
    text: (form && form[s.key]) ? form[s.key] : s.text
  }));
};

const ConsentFormClient = ({ consentForm, onSigned }) => {
  const sections = buildSections(consentForm);

  const [formData, setFormData] = useState({
    client_full_name: '',
    client_date_of_birth: '',
    client_address: '',
    client_phone: '',
    client_email: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    therapy_goals: '',
    client_signature: '',
    signature_date: format(new Date(), 'yyyy-MM-dd'),
    additional_notes: ''
  });
  const [acknowledgesAll, setAcknowledgesAll] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const canSubmit = acknowledgesAll && formData.client_signature.trim() && formData.client_full_name.trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    try {
      setSubmitting(true);
      setError('');
      // Set all 6 acknowledgment fields to true (single checkbox covers all)
      const payload = {
        ...formData,
        acknowledges_services: true,
        acknowledges_confidentiality: true,
        acknowledges_risks: true,
        acknowledges_fees: true,
        acknowledges_cancellation: true,
        acknowledges_rights: true,
      };
      await consentFormAPI.sign(consentForm.id, payload);
      onSigned && onSigned();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary mb-1">
          Informed Consent for Counseling Services
        </h2>
        <p className="text-sm text-gray-500 dark:text-dark-text-tertiary">
          Sent by: <strong>{consentForm.partner_name}</strong>
        </p>
      </div>

      {/* Personal Information */}
      <section className="card space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary border-b border-gray-200 dark:border-dark-border pb-3">
          Personal Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="client_full_name"
              value={formData.client_full_name}
              onChange={handleChange}
              required
              className="input w-full"
              placeholder="Your full legal name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
              Date of Birth
            </label>
            <input
              type="date"
              name="client_date_of_birth"
              value={formData.client_date_of_birth}
              onChange={handleChange}
              className="input w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
              Phone Number
            </label>
            <input
              type="tel"
              name="client_phone"
              value={formData.client_phone}
              onChange={handleChange}
              className="input w-full"
              placeholder="Your contact number"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
              Email Address
            </label>
            <input
              type="email"
              name="client_email"
              value={formData.client_email}
              onChange={handleChange}
              className="input w-full"
              placeholder="Your email"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
              Address
            </label>
            <textarea
              name="client_address"
              value={formData.client_address}
              onChange={handleChange}
              rows={2}
              className="input w-full"
              placeholder="Your home address"
            />
          </div>
        </div>
      </section>

      {/* Emergency Contact */}
      <section className="card space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary border-b border-gray-200 dark:border-dark-border pb-3">
          Emergency Contact
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
              Contact Name
            </label>
            <input
              type="text"
              name="emergency_contact_name"
              value={formData.emergency_contact_name}
              onChange={handleChange}
              className="input w-full"
              placeholder="Emergency contact full name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
              Contact Phone
            </label>
            <input
              type="tel"
              name="emergency_contact_phone"
              value={formData.emergency_contact_phone}
              onChange={handleChange}
              className="input w-full"
              placeholder="Emergency contact number"
            />
          </div>
        </div>
      </section>

      {/* Therapy Goals */}
      <section className="card space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary border-b border-gray-200 dark:border-dark-border pb-3">
          Therapy Goals
        </h3>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
            What would you like to work on in therapy?
          </label>
          <textarea
            name="therapy_goals"
            value={formData.therapy_goals}
            onChange={handleChange}
            rows={3}
            className="input w-full"
            placeholder="Describe your therapy goals, concerns, or what you hope to achieve..."
          />
        </div>
      </section>

      {/* Terms & Conditions — read-only */}
      <section className="card space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary border-b border-gray-200 dark:border-dark-border pb-3">
          Terms &amp; Conditions
        </h3>
        <p className="text-sm text-gray-500 dark:text-dark-text-tertiary">
          Please read all sections below carefully before providing your consent.
        </p>
        <div className="space-y-4">
          {sections.map((section, i) => (
            <div key={i} className="bg-gray-50 dark:bg-dark-card-secondary rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 dark:text-dark-text-primary mb-2 text-sm">{section.title}</h4>
              <p className="text-sm text-gray-600 dark:text-dark-text-secondary leading-relaxed">{section.text}</p>
            </div>
          ))}
        </div>

        {/* Single acknowledgment checkbox */}
        <div className={`mt-4 p-4 rounded-lg border-2 transition-colors ${
          acknowledgesAll
            ? 'border-green-400 bg-green-50 dark:bg-green-900/20 dark:border-green-600'
            : 'border-gray-300 dark:border-dark-border bg-white dark:bg-dark-card'
        }`}>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={acknowledgesAll}
              onChange={(e) => setAcknowledgesAll(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 flex-shrink-0"
            />
            <span className="text-sm font-medium text-gray-800 dark:text-dark-text-primary leading-relaxed">
              I have read and understood all of the above terms and conditions and consent to proceed with therapy.
            </span>
          </label>
        </div>
      </section>

      {/* Additional Notes */}
      <section className="card space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary border-b border-gray-200 dark:border-dark-border pb-3">
          Additional Notes <span className="font-normal text-gray-400 text-sm">(Optional)</span>
        </h3>
        <textarea
          name="additional_notes"
          value={formData.additional_notes}
          onChange={handleChange}
          rows={3}
          className="input w-full"
          placeholder="Any additional information you'd like to share with your therapist..."
        />
      </section>

      {/* Digital Signature */}
      <section className="card space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary border-b border-gray-200 dark:border-dark-border pb-3">
          Digital Signature
        </h3>
        <p className="text-sm text-gray-600 dark:text-dark-text-secondary">
          By typing your full name below and submitting this form, you confirm that you have read, understood, and agree to the terms outlined in this informed consent document.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
              Type your full name as signature <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="client_signature"
              value={formData.client_signature}
              onChange={handleChange}
              required
              className="input w-full font-semibold italic"
              placeholder="Your full name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
              Date
            </label>
            <input
              type="date"
              name="signature_date"
              value={formData.signature_date}
              className="input w-full"
              readOnly
            />
          </div>
        </div>
      </section>

      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {!canSubmit && (
        <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-700 dark:text-amber-400">
          Please fill in your full name, check the consent checkbox, and type your signature to submit.
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={!canSubmit || submitting}
          className="btn btn-primary px-8 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Submitting...' : 'Submit & Sign Consent Form'}
        </button>
      </div>
    </form>
  );
};

export default ConsentFormClient;
